import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Clock, Video, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

export default function BookConsultation() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/site/settings").then(({ data }) => {
      setUrl(data?.consultation_url || "");
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const isEmbeddable = /calendly\.com|cal\.com/.test(url);

  return (
    <div data-testid="book-consultation-page" className="pt-10 max-w-[1400px] mx-auto px-6 sm:px-12 min-h-screen pb-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="max-w-3xl mx-auto text-center mb-14"
      >
        <p className="label-track text-[#C8A165] mb-4">The Atelier</p>
        <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] leading-[1.05]">
          Book a Design <span className="italic text-[#C8A165]">Consultation.</span>
        </h1>
        <p className="mt-6 text-[#525252] leading-relaxed max-w-xl mx-auto">
          Sit down virtually with one of our jewelry experts for a 30-minute private session.
          We'll discuss your vision, budget, and timing. and sketch first ideas together.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
        {[
          { Icon: Clock, t: "30 min", s: "One-on-one, private" },
          { Icon: Video, t: "Video call", s: "Zoom or Google Meet" },
          { Icon: CalendarCheck, t: "Reschedule", s: "Free, up to 24h prior" },
          { Icon: ShieldCheck, t: "No pressure", s: "Design-first, always" },
        ].map(({ Icon, t, s }) => (
          <div key={t} className="border border-[#EFE7D8] bg-[#FAF7F1] p-5">
            <Icon size={20} strokeWidth={1.4} className="text-[#C8A165] mb-3" />
            <div className="font-heading text-lg text-[#0A0A0A]">{t}</div>
            <div className="text-xs text-[#737373] mt-1">{s}</div>
          </div>
        ))}
      </div>

      <div className="max-w-4xl mx-auto bg-white border border-[#EFE7D8] overflow-hidden">
        {loading ? (
          <div className="h-[720px] flex items-center justify-center text-[#737373]" data-testid="booking-loading">
            Loading availability…
          </div>
        ) : url ? (
          isEmbeddable ? (
            <iframe
              data-testid="booking-iframe"
              src={url}
              title="Book a Consultation"
              className="w-full h-[820px] border-0"
              loading="lazy"
            />
          ) : (
            <div className="p-12 text-center" data-testid="booking-external-cta">
              <p className="font-heading text-2xl text-[#0A0A0A] mb-4">Ready when you are.</p>
              <p className="text-[#737373] mb-8">
                Open our booking calendar in a new tab to reserve your session.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-external-link"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-9 py-4 label-track hover:bg-[#C8A165] transition-colors"
              >
                <CalendarCheck size={14} /> Open Booking Calendar
              </a>
            </div>
          )
        ) : (
          <div className="p-12 text-center" data-testid="booking-empty">
            <p className="font-heading text-2xl text-[#0A0A0A] mb-3">Bookings by request.</p>
            <p className="text-[#737373]">
              Email us at <a href="mailto:atelier@dreamonds.com" className="text-[#C8A165] hover:underline">atelier@dreamonds.com</a> and
              we'll set up your session personally.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
